/** @jsxImportSource @emotion/react */
import { useMemo, useCallback, useRef, useState, useEffect } from 'react';
import { css } from '@emotion/react';
import DOMPurify from 'dompurify';

interface ContentBlock {
  type: 'markdown' | 'html';
  content: string;
}

interface SecureHtmlRendererProps {
  html: string;
  trustedDomains?: string[];
  contentSecurityPolicy?: string;
  adaptiveHeight?: boolean;
}

interface ValidationResult {
  isValid: boolean;
  reason?: string;
}

/**
 * Detects and splits mixed markdown/HTML content into separate blocks
 * @param value - The input string containing mixed content
 * @returns Array of content blocks with their types
 */
function parseContent(value: string): ContentBlock[] {
  const htmlDocRegex = /<!DOCTYPE\s+html>[\s\S]*<\/html>/gi;
  const matches = value.match(htmlDocRegex);

  if (!matches) {
    return [{ type: 'markdown', content: value }];
  }

  const blocks: ContentBlock[] = [];
  let lastIndex = 0;
  const regex = /<!DOCTYPE\s+html>[\s\S]*?<\/html>/gi;
  let match;

  while ((match = regex.exec(value)) !== null) {
    // Add markdown content before this HTML block
    if (match.index > lastIndex) {
      const markdownContent = value.substring(lastIndex, match.index).trim();
      if (markdownContent) {
        blocks.push({ type: 'markdown', content: markdownContent });
      }
    }

    blocks.push({ type: 'html', content: match[0] });
    lastIndex = regex.lastIndex;
  }

  // Add any remaining markdown content
  if (lastIndex < value.length) {
    const markdownContent = value.substring(lastIndex).trim();
    if (markdownContent) {
      blocks.push({ type: 'markdown', content: markdownContent });
    }
  }

  return blocks;
}

/**
 * Validates a URL against trusted domains
 * @param url - URL string to validate
 * @param trustedDomains - Array of trusted domain names
 * @returns Validation result
 */
function validateUrl(url: string, trustedDomains: string[]): ValidationResult {
  try {
    const urlObj = new URL(url);
    const isValidTrustedDomain = trustedDomains.some((domain) => {
      return urlObj.hostname === domain || urlObj.hostname.endsWith('.' + domain);
    });

    if (!isValidTrustedDomain) {
      return {
        isValid: false,
        reason: `Untrusted external resource: ${urlObj.hostname}`,
      };
    }

    return { isValid: true };
  } catch {
    return {
      isValid: false,
      reason: `Invalid URL: ${url}`,
    };
  }
}

/**
 * Validates external resources in HTML against trusted domains
 * @param cleanHtmlString - Sanitized HTML content
 * @param trustedDomains - Array of trusted domain names
 * @returns Validation result
 */
function validateExternalResources(cleanHtmlString: string, trustedDomains: string[]): ValidationResult {
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = cleanHtmlString;
  const externalResources = tempDiv.querySelectorAll('[src], [href]');

  for (const element of externalResources) {
    const url = element.getAttribute('src') || element.getAttribute('href');

    if (!url || !url.startsWith('http')) {
      continue;
    }

    const validation = validateUrl(url, trustedDomains);
    if (!validation.isValid) {
      return validation;
    }
  }

  return { isValid: true };
}

/**
 * Validates HTML content for security using DOMPurify and domain whitelisting
 * @param html - HTML content to validate
 * @param trustedDomains - Array of trusted domain names (supports '*' for all domains)
 * @returns Validation result with success status and optional error reason
 */
function validateHtmlContent(html: string, trustedDomains: string[]): ValidationResult {
  try {
    // Use DOMPurify to clean and validate structure - more permissive for AI-generated content
    const cleanHtmlString = DOMPurify.sanitize(html, {
      WHOLE_DOCUMENT: false, // Allow partial HTML
      SANITIZE_DOM: true,
      KEEP_CONTENT: true, // Keep more content
      ADD_TAGS: ['script', 'link', 'style', 'meta', 'title'],
      FORBID_ATTR: ['onload', 'onerror', 'onclick', 'onmouseover', 'onmouseout'],
      FORBID_TAGS: ['object', 'embed', 'applet', 'form', 'input', 'button', 'select', 'textarea'],
      ALLOWED_URI_REGEXP:
        /^(?:(?:(?:f|ht)tps?|mailto|tel|callto|cid|xmpp|data):|[^a-z]|[a-z+.\-]+(?:[^a-z+.\-:]|$))/i,
    });

    // Skip domain validation if wildcard is present
    const hasWildcard = trustedDomains.includes('*');

    if (hasWildcard) {
      return { isValid: true };
    }

    // Validate external resources against trusted domains
    return validateExternalResources(cleanHtmlString, trustedDomains);
  } catch (error) {
    return {
      isValid: false,
      reason: `HTML validation failed: ${(error as Error).message}`,
    };
  }
}

/**
 * Sanitizes HTML content by removing dangerous elements while preserving scripts
 * Uses minimal sanitization approach for trusted content
 * @param html - HTML content to sanitize
 * @returns Sanitized HTML string
 */
function sanitizeHtmlContent(html: string): string {
  return html
    .replace(/<(object|embed|applet)[^>]*>.*?<\/\1>/gis, '') // Remove dangerous plugins
    .replace(/on\w+\s*=\s*["'][^"']*["']/gi, ''); // Remove inline event handlers
}

/**
 * Adds security headers to HTML content
 * @param html - HTML content
 * @param contentSecurityPolicy - CSP string
 * @returns HTML with security headers added
 */
function addSecurityHeaders(html: string, contentSecurityPolicy: string): string {
  const heightReportScript = `<script>
  (function() {
    function reportHeight() {
      var h = Math.max(
        document.body ? document.body.scrollHeight : 0,
        document.body ? document.body.offsetHeight : 0,
        document.documentElement ? document.documentElement.scrollHeight : 0,
        document.documentElement ? document.documentElement.offsetHeight : 0,
        document.documentElement ? document.documentElement.clientHeight : 0
      );
      if (h > 0) { window.parent.postMessage({ type: 'iframe-resize', height: h }, '*'); }
    }
    window.addEventListener('load', reportHeight);
    if (typeof ResizeObserver !== 'undefined') {
      new ResizeObserver(reportHeight).observe(document.documentElement);
    }
  })();
<\/script>`;

  let result: string;

  if (html.includes('<head')) {
    result = html.replace(
      /<head[^>]*>/i,
      `<head>
        <meta http-equiv="Content-Security-Policy" content="${contentSecurityPolicy}">
        <meta http-equiv="X-Content-Type-Options" content="nosniff">
        <meta http-equiv="Referrer-Policy" content="no-referrer">`
    );
  } else {
    // If no head tag, wrap the content in a complete HTML document
    return `<!DOCTYPE html>
<html>
<head>
  <meta http-equiv="Content-Security-Policy" content="${contentSecurityPolicy}">
  <meta http-equiv="X-Content-Type-Options" content="nosniff">
  <meta http-equiv="Referrer-Policy" content="no-referrer">
  <title>Secure Content</title>
  <style>
    body { font-family: Arial, sans-serif; margin: 20px; }
    img { max-width: 100%; height: auto; }
    table { border-collapse: collapse; width: 100%; }
    th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
    th { background-color: #f2f2f2; }
  </style>
</head>
<body>
  ${html}
  ${heightReportScript}
</body>
</html>`;
  }

  // Inject height script before </body> for full documents
  if (result.includes('</body>')) {
    return result.replace(/<\/body>/i, `${heightReportScript}</body>`);
  }
  return result + heightReportScript;
}

/**
 * Creates a blob URL from HTML content
 * @param html - HTML content
 * @returns Blob URL or null if creation fails
 */
function createHtmlBlobUrl(html: string): string | null {
  try {
    const blob = new Blob([html], { type: 'text/html' });
    return URL.createObjectURL(blob);
  } catch (error) {
    console.error('Failed to create blob URL:', error);
    return null;
  }
}

/**
 * Component to render HTML content in a secure sandboxed iframe
 * Validates external resources against trusted domains and applies CSP
 */
const SecureHtmlRenderer = ({
  html,
  contentSecurityPolicy,
  adaptiveHeight = true,
  trustedDomains = [],
}: SecureHtmlRendererProps) => {
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const [frameHeight, setFrameHeight] = useState(400);

  useEffect(() => {
    if (!adaptiveHeight) return;

    const handleMessage = (event: MessageEvent<unknown>) => {
      if (event.source !== iframeRef.current?.contentWindow) return;
      const data = event.data as { type?: unknown; height?: unknown };
      if (data?.type === 'iframe-resize' && typeof data.height === 'number' && data.height > 0) {
        setFrameHeight(data.height);
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [adaptiveHeight]);
  // Internal CSP creation function - kept private to component
  const createContentSecurityPolicy = useCallback((domains: string[]): string => {
    // Handle wildcard case
    const hasWildcard = domains.includes('*');

    if (hasWildcard) {
      // Permissive CSP for wildcard domains - allows external resources
      return [
        `default-src 'self' * 'unsafe-inline' 'unsafe-eval'`,
        `script-src 'self' * 'unsafe-inline' 'unsafe-eval'`,
        `style-src 'self' * 'unsafe-inline'`,
        `font-src 'self' * data:`,
        `img-src 'self' * data: blob:`,
        `connect-src 'self' *`,
        `frame-src 'self' *`,
        `object-src 'self' *`,
        `media-src 'self' *`,
        `worker-src 'self' *`,
      ].join('; ');
    }

    // Normal domain-specific CSP
    const domainList = domains
      .filter(Boolean)
      .map((d) => `https://${d}`)
      .join(' ');

    const cspParts = [
      `default-src 'self'`,
      `script-src 'self'`,
      `style-src 'self'`,
      `font-src 'self' data:`,
      `img-src 'self' data:`,
      `connect-src 'self'`,
      `frame-src 'none'`,
      `object-src 'none'`,
    ];

    // Add domains to relevant directives if any exist
    if (domainList) {
      cspParts[0] += ` ${domainList}`; // default-src
      cspParts[1] += ` ${domainList}`; // script-src
      cspParts[2] += ` ${domainList}`; // style-src
      cspParts[3] += ` ${domainList}`; // font-src
      cspParts[4] += ` ${domainList}`; // img-src
      cspParts[5] += ` ${domainList}`; // connect-src
    }

    return cspParts.join('; ');
  }, []);

  // Pre-compute default CSP if trustedDomains is stable
  const defaultCSP = useMemo(
    () => createContentSecurityPolicy(trustedDomains),
    [createContentSecurityPolicy, trustedDomains]
  );

  const { secureHtml, isValid, validationError } = useMemo(() => {
    // Validate HTML content
    const validation = validateHtmlContent(html, trustedDomains);

    if (!validation.isValid) {
      return {
        secureHtml: '',
        isValid: false,
        validationError: validation.reason,
      };
    }

    try {
      // Sanitize and secure the HTML
      const sanitizedHtml = sanitizeHtmlContent(html);
      const csp = contentSecurityPolicy || defaultCSP;
      const secureHtmlWithHeaders = addSecurityHeaders(sanitizedHtml, csp);

      return {
        secureHtml: secureHtmlWithHeaders,
        isValid: true,
        validationError: null,
      };
    } catch (error) {
      return {
        secureHtml: '',
        isValid: false,
        validationError: `Failed to process HTML: ${(error as Error).message}`,
      };
    }
  }, [html, trustedDomains, contentSecurityPolicy, defaultCSP]);

  const blobUrl = useMemo(() => {
    if (!isValid || !secureHtml) {
      return null;
    }
    const url = createHtmlBlobUrl(secureHtml);
    return url;
  }, [secureHtml, isValid]);

  // Render blank iframe for invalid content or blob creation failure
  if (!isValid || !blobUrl) {
    const blankHtml =
      '<!DOCTYPE html><html><head><title>Content Unavailable</title></head><body></body></html>';
    const blankBlobUrl = createHtmlBlobUrl(blankHtml);

    if (!blankBlobUrl) {
      return (
        <div css={htmlContainerStyles}>
          <div css={errorFallbackStyles}>Content could not be rendered safely</div>
        </div>
      );
    }

    return (
      <div css={htmlContainerStyles}>
        <iframe
          ref={iframeRef}
          src={blankBlobUrl}
          css={iframeStyles}
          title="Content Unavailable"
          sandbox=""
          referrerPolicy="no-referrer"
          style={adaptiveHeight ? { height: `${frameHeight}px` } : undefined}
          onLoad={() => {
            setTimeout(() => URL.revokeObjectURL(blankBlobUrl), 1000);
          }}
        />
      </div>
    );
  }

  return (
    <div css={htmlContainerStyles}>
      <iframe
        ref={iframeRef}
        src={blobUrl}
        css={iframeStyles}
        title="Secure HTML Content"
        sandbox="allow-scripts"
        referrerPolicy="no-referrer"
        style={adaptiveHeight ? { height: `${frameHeight}px` } : undefined}
        onLoad={() => {
          setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
        }}
        onError={() => {
          console.error('Failed to load HTML content in iframe');
        }}
      />
    </div>
  );
};

// Styles
const mixedContentContainerStyles = css({
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
});

const markdownBlockStyles = css({
  // Markdown content inherits existing styles
});

const htmlBlockStyles = css({
  // HTML content container
});

const htmlContainerStyles = css({
  width: '100%',
  border: 'none',
  borderRadius: 0,
  overflow: 'visible',
  backgroundColor: 'transparent',
  padding: 0,
});

const iframeStyles = css({
  width: '100%',
  border: 'none',
  display: 'block',
  backgroundColor: 'transparent',
  overflow: 'hidden',
});

const errorContainerStyles = css({
  width: '100%',
  border: '1px solid #f5c6cb',
  borderRadius: '4px',
  backgroundColor: '#f8d7da',
  padding: '16px',
});

const errorMessageStyles = css({
  color: '#721c24',
  marginBottom: '8px',
});

const errorFallbackStyles = css({
  padding: '20px',
  textAlign: 'center',
  color: '#666',
  fontSize: '14px',
});

const detailsStyles = css({
  marginTop: '8px',
  '& summary': {
    cursor: 'pointer',
    color: '#721c24',
    fontSize: '14px',
  },
});

const preStyles = css({
  backgroundColor: '#ffffff',
  border: '1px solid #dee2e6',
  borderRadius: '4px',
  padding: '8px',
  fontSize: '12px',
  overflow: 'auto',
  maxHeight: '200px',
  marginTop: '8px',
});

export {
  SecureHtmlRenderer,
  parseContent,
  mixedContentContainerStyles,
  markdownBlockStyles,
  htmlBlockStyles,
  htmlContainerStyles,
  iframeStyles,
  errorContainerStyles,
  errorMessageStyles,
  detailsStyles,
  preStyles,
};
